import { Dispatch, SetStateAction } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Heading,
  Link,
  Stack,
  Text,
  useBreakpoint
} from "@seneca/senekit";

import { linkToTransferPremiumForm } from "common/links";

import { ChildrenType } from "seneca-common/features/parents/features/parent/service/hooks/types";
import { useFetchConnectedChildren } from "seneca-common/features/parents/features/parent/service/hooks/useFetchChildConnections";
import { useGetChildrenActiveSubscriptions } from "seneca-common/features/parents/features/parent/service/hooks/useFetchChildrenSubscriptions";
import useReassignSubscription from "seneca-common/features/parents/features/parent/service/hooks/useReassignSubscription";
import { getLiveStripeSubscriptionId } from "seneca-common/features/subscriptions/state";
import { getUserEmail } from "seneca-common/features/user/state";

import TextPlaceholder, {
  MultiLineTextPlaceholder
} from "features/common/components/TextPlaceholder";

import NoChildrenPage from "./NoChildrenPage";
import SelectChildCard from "./SelectChildCard";

export default function ChooseAccountPage({
  selectedChild,
  setSelectedChild,
  onSubmitSuccess,
  onSubmitError
}: {
  selectedChild?: ChildrenType;
  setSelectedChild: Dispatch<SetStateAction<ChildrenType | undefined>>;
  onSubmitSuccess: () => void;
  onSubmitError: () => void;
}) {
  const { isMobileOnly } = useBreakpoint();

  const userEmail = useSelector(getUserEmail);
  const subscriptionId = useSelector(getLiveStripeSubscriptionId);

  const selectedChildId = selectedChild?.userId;
  const disabledCTA = !selectedChildId || !subscriptionId;

  const { data: children = [], isInitialLoading: childrenFetching } =
    useFetchConnectedChildren();
  const { isInitialLoading: subscriptionsFetching } =
    useGetChildrenActiveSubscriptions();
  const isLoading = childrenFetching || subscriptionsFetching;

  const { mutateAsync: reassignSubscription, isPending: isTransferring } =
    useReassignSubscription();

  if (!isLoading && children.length === 0) return <NoChildrenPage />;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      paddingTop={["none", "xlarge"]}
      paddingX={["none", "large"]}
      minHeight={["100%", "400px"]}
      alignItems="center"
    >
      <Stack space="xlarge">
        <TextPlaceholder
          loading={isLoading}
          placeholderHeight="2rem"
          placeholderWidth={isMobileOnly ? "80%" : "50%"}
        >
          <Heading level="4">Transfer subscription</Heading>

          <Box marginTop="xlarge">
            <Text>
              You currently have a Premium subscription associated with your
              account. Make use of this by choosing which connected account to
              transfer it to.
            </Text>
          </Box>
        </TextPlaceholder>
        <Stack space="large">
          <MultiLineTextPlaceholder
            numberOfLines={2}
            placeholderHeight="3rem"
            placeholderWidth="100%"
            loading={isLoading}
          />
          {!isLoading &&
            children.map(child => (
              <SelectChildCard
                key={child.userId}
                child={child}
                selected={child.userId === selectedChildId}
                onClick={() => setSelectedChild(child)}
              />
            ))}
        </Stack>
        <TextPlaceholder
          loading={isLoading}
          placeholderHeight="1rem"
          placeholderWidth={isMobileOnly ? "60%" : "40%"}
        >
          <Link to={linkToTransferPremiumForm(userEmail)}>
            💳 Transfer subscription to a different account
          </Link>
        </TextPlaceholder>
      </Stack>

      <Box paddingY="xlarge" />
      <TextPlaceholder
        placeholderHeight="3rem"
        placeholderWidth={isMobileOnly ? "100%" : "16rem"}
        loading={isLoading}
      >
        <Button
          width={["100%", "auto"]}
          disabled={disabledCTA}
          loading={isTransferring}
          onClick={() => {
            if (!disabledCTA) {
              reassignSubscription(
                {
                  reassignToUserId: selectedChildId,
                  subscriptionId: subscriptionId!
                },
                {
                  onSuccess: onSubmitSuccess,
                  onError: onSubmitError
                }
              );
            }
          }}
        >
          Confirm transfer
        </Button>
      </TextPlaceholder>
    </Box>
  );
}
